import * as React from 'react';
import { getCurrentDatePlusDays } from '@/utils/date';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import {
  pickpackTypesByVytalType,
  VytalStoreTypes,
} from '../../views/MerchantComponents/StoreTypeSelector';
import { EditItem, ResponsiveTable } from '../Layout';
import {
  MerchantOrderItemOption,
  Revision,
  MerchantOrderItemStatus,
  MerchantOrderType,
  MerchantOrderStatus,
  isOnboarding,
  NotChangeableMerchantOrderStatus,
} from './HandleStatusChange';
import { getItemIndex, isContainerCountAllowedForOnboarding } from './Utils';
import { ContainerType } from '../../views/ContainerComponents/ContainerPage';
import { Communicator } from '../../tools/Communication';
import { ContentSection } from '../Layout/ContentSection';

/**
 * Only to be used in EditMerchantOrder
 */
export const ItemTable = function ItemTable(props: {
  orderItemOptions: MerchantOrderItemOption[];
  revision: Revision;
  earliestOrderExecutionDateByType: {
    [id: number]: Date | null;
  };
  setEarliestOrderExecutionDateByType: React.Dispatch<
    React.SetStateAction<{
      [id: number]: Date | null;
    }>
  >;
  earliestOrderExecutionDate: Date;
  setEarliestOrderExecutionDate: React.Dispatch<React.SetStateAction<Date>>;
  setRevision: React.Dispatch<React.SetStateAction<Revision>>;
  orderId: string | null;
  ogRevisionStatus: number;
  storeType: string;
  showCurrentStock: boolean;
  canAccessPartnerData: boolean;
}) {
  const {
    orderItemOptions,
    revision,
    earliestOrderExecutionDateByType,
    setEarliestOrderExecutionDateByType,
    earliestOrderExecutionDate,
    setEarliestOrderExecutionDate,
    setRevision,
    orderId,
    ogRevisionStatus,
    storeType,
    showCurrentStock,
    canAccessPartnerData,
  } = props;

  const [containerTypes, setContainerTypes] = React.useState<ContainerType[]>(
    [],
  );

  React.useEffect(() => {
    Communicator.Get('/Container/GetAllContainerCategories', {})
      .then((result) => result.json())
      .then((data) => {
        if (Array.isArray(data.containerTypes)) {
          setContainerTypes(data.containerTypes);
        }
      });
  }, []);
  function datesAreOnSameDay(first: Date, second: Date) {
    return (
      first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate()
    );
  }

  function setSelectedAmount(
    status: number,
    index: number,
    typeId: number,
    value: string,
    disabledUntil?: string,
  ) {
    const revisionTypeId = parseInt(String(revision.type), 10);
    const items = [...revision.items];
    index = getItemIndex(status, typeId, items);
    console.log({ index, status, typeId, items, value, disabledUntil });
    let numberValue = value.replace(/[^0-9.]/g, '');
    if (
      revisionTypeId !== MerchantOrderType.Special &&
      parseInt(numberValue) > 500
    ) {
      numberValue = '500';
    } else if (parseInt(numberValue) < 0) {
      numberValue = '0';
    }
    const merchantOrderTypeId = parseInt(revision.type as string);
    if (isOnboarding(merchantOrderTypeId) && typeId > 0) {
      if (
        !isContainerCountAllowedForOnboarding(
          merchantOrderTypeId,
          items,
          storeType,
          typeId,
          parseInt(numberValue),
        )
      ) {
        numberValue = '0';
      }
    }
    items[index] = { ...items[index], amount: numberValue };
    const newRevision = { ...revision };
    newRevision.items = items;
    const ignoreStateUpdateStates = [
      MerchantOrderType.Sample,
      MerchantOrderType.EventsSample,
      MerchantOrderType.Special,
      MerchantOrderType.Onboarding,
      MerchantOrderType.OnboardingL,
      MerchantOrderType.OnboardingM,
      MerchantOrderType.OnboardingS,
      MerchantOrderType.DPDPickup,
      MerchantOrderType.DPDShopReturn,
      // keep selection from changing type for warehouse transfer
      MerchantOrderType.WarehouseTransfer,
      // an existing Pickup or Both order keeps its type; neither is creatable
      MerchantOrderType.Pickup,
      MerchantOrderType.Both,
    ];
    // Every type that reaches here has its pickup column disabled, so an amount
    // edit can only mean a delivery — which is a Resupply.
    if (!ignoreStateUpdateStates.includes(revisionTypeId)) {
      newRevision.type = MerchantOrderType.Resupply;
    }
    if (status == MerchantOrderItemStatus.Delivery && disabledUntil != null) {
      const earliestDateByType = {
        ...earliestOrderExecutionDateByType,
      };
      if (numberValue !== '0') {
        let earliestDate = new Date(disabledUntil);
        // add the time zone offset that is applied when creating the date
        // so later when we look at the UTC time it still matches to disabledUntil
        earliestDate = new Date(
          earliestDate.getTime() - earliestDate.getTimezoneOffset() * 60000,
        );
        earliestDateByType[typeId] = earliestDate;
      } else {
        earliestDateByType[typeId] = null;
      }
      const newEarliestDate = Object.values(earliestDateByType)
        .map((d) => d || getCurrentDatePlusDays(1))
        .reduce(
          (max, cur) => (max > cur ? max : cur),
          getCurrentDatePlusDays(1),
        );
      newRevision.orderExecutionDate =
        newRevision.orderExecutionDate > newEarliestDate
          ? newRevision.orderExecutionDate
          : newEarliestDate;
      if (!datesAreOnSameDay(newEarliestDate, earliestOrderExecutionDate)) {
        alert(
          i18n.t('MerchantOrder:alertEarliestOrderExecutionDateChanged', {
            date: newEarliestDate.toLocaleDateString(
              i18n.language,
              getDateFormatOptions(false),
            ),
          }),
        );
      }
      setEarliestOrderExecutionDate(newEarliestDate);
      setEarliestOrderExecutionDateByType(earliestDateByType);
    }
    setRevision(newRevision);
  }

  function getPickupDisabled(item: MerchantOrderItemOption) {
    const typeId = parseInt(String(revision.type), 10);
    if (item.pickupDisabled || !canAccessPartnerData) {
      return true;
    }
    // Only an existing Pickup order still edits pickup amounts. A new order can
    // no longer be a Pickup, so nothing it collects belongs in this column.
    if (typeId !== MerchantOrderType.Pickup) {
      return true;
    }
    if (NotChangeableMerchantOrderStatus.includes(ogRevisionStatus)) {
      return true;
    }
    return false;
  }

  function getResupplyDisabled(item: MerchantOrderItemOption) {
    const allowResupplyStates = [
      MerchantOrderType.Resupply,
      MerchantOrderType.Sample,
      MerchantOrderType.EventsSample,
      MerchantOrderType.Special,
      MerchantOrderType.Onboarding,
      MerchantOrderType.OnboardingL,
      MerchantOrderType.OnboardingM,
      MerchantOrderType.OnboardingS,
      // treat warehouse transfer like delivery/resupply
      MerchantOrderType.WarehouseTransfer,
    ];
    const typeId = parseInt(String(revision.type), 10);
    if (item.resupplyDisabled) {
      return true;
    }
    if (orderId != null && !allowResupplyStates.includes(typeId)) {
      return true;
    }
    if (
      typeId !== MerchantOrderType.Both &&
      typeId !== MerchantOrderType.Pickup &&
      !allowResupplyStates.includes(typeId)
    ) {
      return true;
    }
    if (NotChangeableMerchantOrderStatus.includes(ogRevisionStatus)) {
      return true;
    }
    // !careful: here we return false
    if (
      typeId === MerchantOrderType.Sample ||
      typeId === MerchantOrderType.EventsSample ||
      typeId === MerchantOrderType.Special ||
      pickpackTypesByVytalType[VytalStoreTypes.Internal].includes(storeType)
    ) {
      return false;
    }
    return false;
  }

  function getIsDisabled(item: MerchantOrderItemOption, status: number) {
    if (status === MerchantOrderItemStatus.Delivery) {
      return getResupplyDisabled(item);
    } else if (status === MerchantOrderItemStatus.Pickup) {
      return getPickupDisabled(item);
    }
    return false;
  }

  function getResupplyOptions(item: MerchantOrderItemOption) {
    let result = [
      parseInt(
        (
          revision.items[
            getItemIndex(
              MerchantOrderItemStatus.Delivery,
              item.typeId,
              revision.items,
            )
          ] || { amount: 0 }
        ).amount as string,
      ),
    ];
    const typeId = parseInt(String(revision.type), 10);
    if (
      typeId === MerchantOrderType.Sample ||
      typeId === MerchantOrderType.EventsSample
    ) {
      if (item.typeId > 0) {
        result = result.concat([0, 1, 2, 3]);
      } else {
        result = result.concat([0]);
      }
    } else {
      result = result.concat(item.resupplyOptions);
    }
    return [...result]
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .sort((a: any, b: any) => parseInt(a) - parseInt(b))
      .map((a) => (
        <option key={a} value={a}>
          {a}
        </option>
      ));
  }

  function getResupplyColumn(index: number, item: MerchantOrderItemOption) {
    const typeId = parseInt(String(revision.type), 10);
    if (typeId === MerchantOrderType.Special) {
      return getAmountColumn(index, item, MerchantOrderItemStatus.Delivery);
    }
    return (
      <select
        className="form-control"
        onChange={(e) =>
          setSelectedAmount(
            MerchantOrderItemStatus.Delivery,
            index,
            item.typeId,
            e.target.value,
            item.resupplyDisabledUntil,
          )
        }
        value={
          (
            revision.items[
              getItemIndex(
                MerchantOrderItemStatus.Delivery,
                item.typeId,
                revision.items,
              )
            ] || { amount: 0 }
          ).amount
        }
        disabled={getResupplyDisabled(item)}
      >
        {getResupplyOptions(item)}
      </select>
    );
  }

  function getAmountColumn(
    index: number,
    item: MerchantOrderItemOption,
    status: number,
  ) {
    return (
      <input
        className="form-control"
        onChange={(e) =>
          setSelectedAmount(status, index, item.typeId, e.target.value)
        }
        value={
          (
            revision.items[
              getItemIndex(status, item.typeId, revision.items)
            ] || {
              amount: 0,
            }
          ).amount
        }
        disabled={getIsDisabled(item, status)}
      />
    );
  }

  function hasOrderedAmount(typeId: number) {
    return revision.items.some(
      (i) => i.typeId === typeId && parseInt(String(i.amount), 10) > 0,
    );
  }

  function getOrderItemOptions() {
    const options = orderItemOptions.filter((item) => {
      if (item.typeId < 0) {
        // A custom option with resupply and pickup both switched off can never get
        // an amount here, so it only clutters the table — unless an existing order
        // already carries one for it.
        return (
          !(item.resupplyDisabled && item.pickupDisabled) ||
          hasOrderedAmount(item.typeId)
        );
      }
      if (item.typeId === 0) {
        return true;
      }
      const type = parseInt(revision.type as string, 10);
      const containerType = containerTypes.find((ct) => ct.id === item.typeId);
      if (type !== MerchantOrderType.EventsSample) {
        return containerType?.class != 'EventPortfolio';
      } else {
        return containerType?.class == 'EventPortfolio';
      }
    });
    return options.map((item, index) => {
      return (
        <tr key={item.typeId}>
          <td>{item.name}</td>
          {showCurrentStock && (
            <td>
              {item.currentStock ||
                (item.typeId > 0 ? 0 : i18n.t('MerchantOrder:na'))}
            </td>
          )}
          <td>
            {getAmountColumn(index, item, MerchantOrderItemStatus.Pickup)}
          </td>
          <td>{getResupplyColumn(index, item)}</td>
        </tr>
      );
    });
  }

  return (
    <ContentSection title={i18n.t('MerchantOrder:items')}>
      <ResponsiveTable className="mt-4" fixed={true}>
        <thead>
          <tr>
            <th>{i18n.t('Common:name')}</th>
            {showCurrentStock && (
              <th>{i18n.t('MerchantOrder:currentStock')}</th>
            )}
            <th>{i18n.t('MerchantOrder:pickup')}</th>
            <th>{i18n.t('MerchantOrder:delivery')}</th>
          </tr>
        </thead>
        <tbody>{getOrderItemOptions()}</tbody>
      </ResponsiveTable>
    </ContentSection>
  );
};
